import React, { useMemo, useEffect } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { graphql, useLazyLoadQuery } from 'react-relay'

import {
  Route,
  Routes,
  useLocation,
  useMatch,
  useParams,
  useNavigate,
} from 'react-router-dom'

import { lazy as loadable } from '@loadable/component'

import { SSRNavigate } from '@newComponents/SSRNavigate'
import { GuildMetadata } from '@newComponents/Guild/GuildMetadata'
import { wrapSuspenseScreen } from '@newComponents/SuspenseWrapper'
import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'

import { usePaletteEffect } from '@newComponents/Palette'
import { GuildCard } from '@newComponents/Guild/GuildCard'
import { Text } from '@newComponents/Text'
import { Icon } from '@newComponents/Icon'
import { NotFoundScreen } from '@newComponents/NotFoundScreen'
import { Grid } from '@newComponents/Grid'
import { Group } from '@newComponents/Group'
import { Link } from '@newComponents/Link'
import { IconWrapper } from '@newComponents/Icon/IconWrapper'
import { MobileNavDropdown } from '@newComponents/MobileNavDropdown'

import { YStack, XStack } from 'tamagui'

import { Button } from '@newComponents/Button/Button'
import { Screen } from '@newComponents/Screen'

import {
  GuildNavigatorQuery,
  GuildNavigatorQuery$data,
} from './__generated__/GuildNavigatorQuery.graphql'

const NavigationIcon = loadable(
  () => import('../../newComponents/Icon/icons/NavigationIcon')
)

const GuildNetworksScreen = wrapSuspenseScreen(
  loadable(
    () => import('../../screens/GuildNetworksScreen/GuildNetworksScreen')
  )
)

const GuildEventsScreen = wrapSuspenseScreen(
  loadable(() => import('../../screens/GuildEventsScreen/GuildEventsScreen'))
)

const GuildPresentationsScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import('../../screens/GuildPresentationsScreen/GuildPresentationsScreen')
  )
)

const GuildMembersScreen = wrapSuspenseScreen(
  loadable(() => import('../../screens/GuildMembersScreen/GuildMembersScreen'))
)

const GuildDonationScreen = wrapSuspenseScreen(
  loadable(
    () => import('../../screens/GuildDonationScreen/GuildDonationScreen')
  )
)

const GuildDonationConfirmationScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import(
        '../../screens/GuildDonationConfirmationScreen/GuildDonationConfirmationScreen'
      )
  )
)

const GuildInviteScreen = wrapSuspenseScreen(
  loadable(() => import('../../screens/GuildInviteScreen/GuildInviteScreen'))
)

const GuildSettingsNavigator = wrapSuspenseScreen(
  loadable(() => import('../GuildSettingsNavigator'))
)

const GuildBentoScreen = wrapSuspenseScreen(
  loadable(
    () => import('../../screens/GuildBentoScreen/GuildBentoScreen')
  )
)

export const GuildNavigator = () => {
  const params = useParams()

  const data = useLazyLoadQuery<GuildNavigatorQuery>(
    graphql`
      query GuildNavigatorQuery($id: String!) {
        nodeBySlugId(id: $id) {
          __typename

          ... on Guild {
            id
            slugId
            type

            primaryPhoto {
              colorPalette
            }

            myMembership {
              id
            }

            minimumDonationAmount
            donationCurrency

            ...GuildCardNew_guild
            ...GuildMetadata_guild

            totalNetworks: rankedNetworks(
              membershipStatus: ACCEPTED
              first: 1
            ) {
              edges {
                cursor
              }
            }

            totalGuilds: rankedNetworkGuilds(
              membershipStatus: ACCEPTED
              first: 1
            ) {
              edges {
                cursor
              }
            }

            totalEvents: events(first: 1) {
              edges {
                cursor
              }
            }

            totalPresentations: presentations(first: 1) {
              edges {
                cursor
              }
            }
          }
        }
      }
    `,
    { id: params.actorId || '' }
  )

  const typenameIsGuild = data.nodeBySlugId?.__typename === 'Guild'

  usePaletteEffect(
    typenameIsGuild ? data.nodeBySlugId.primaryPhoto?.colorPalette : null
  )

  const location = useLocation()

  const locationState = location.state as { backgroundLocation?: Location }

  if (data.nodeBySlugId?.__typename !== 'Guild') {
    return <NotFoundScreen />
  }

  return (
    <>
      <GuildMetadata guild={data.nodeBySlugId} />

      <Routes location={locationState?.backgroundLocation || location}>
        <Route path='settings/*' element={<GuildSettingsNavigator />} />

        <Route
          path='*'
          element={
            <GuildNavigatorSidebar key={params.actorId} data={data}>
              <Routes location={locationState?.backgroundLocation || location}>
                <Route path='network' element={<GuildNetworksScreen />} />

                <Route path='events/*' element={<GuildEventsScreen />} />

                <Route
                  path='presentations'
                  element={<GuildPresentationsScreen />}
                />
                <Route path='members' element={<GuildMembersScreen />} />
                <Route path='donate' element={<GuildDonationScreen />} />
                <Route
                  path='donate/confirm'
                  element={<GuildDonationConfirmationScreen />}
                />

                <Route path='invite' element={<GuildInviteScreen />} />

                <Route
                  index
                  element={<GuildBentoScreen />}
                />
                <Route
                  path='*'
                  element={<SSRNavigate to={`/${params.actorId}`} replace />}
                />
              </Routes>
            </GuildNavigatorSidebar>
          }
        />
      </Routes>
    </>
  )
}

const GuildNavigatorCardSection = ({
  data,
}: {
  data: GuildNavigatorQuery$data
}) => {
  const { t } = useLingui()
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  // useMatch matches against location data which encodes any unicode slugs
  const actorIdMatch = encodeURIComponent(params.actorId as string)
  const homeMatch = useMatch(`/${actorIdMatch}`)
  const networkMatch = useMatch(`/${actorIdMatch}/network`)
  const eventsMatch = useMatch(`/${actorIdMatch}/events/*`)
  const presentationsMatch = useMatch(`/${actorIdMatch}/presentations`)
  const membersMatch = useMatch(`/${actorIdMatch}/members`)

  const selectedIndex = useMemo<number>(() => {
    if (data.nodeBySlugId?.__typename !== 'Guild') {
      return 0
    }

    const hasEvents = !!data.nodeBySlugId.totalEvents.edges.length
    const hasPresentations = !!data.nodeBySlugId.totalPresentations.edges.length

    // Home is always index 0
    if (homeMatch) {
      return 0
    }

    // All other tabs shift by +1 to account for Home at index 0
    if (
      data.nodeBySlugId.type === 'NETWORK' &&
      data.nodeBySlugId.totalGuilds.edges.length
    ) {
      if (networkMatch) {
        return 1
      }

      if (eventsMatch) {
        return 2
      }

      if (presentationsMatch) {
        if (hasEvents) {
          return 3
        }

        return 2
      }

      if (membersMatch) {
        if (hasEvents && hasPresentations) {
          return 4
        }

        if (hasEvents || hasPresentations) {
          return 3
        }

        return 2
      }
    }

    if (eventsMatch) {
      return 1
    }

    if (presentationsMatch) {
      if (hasEvents) {
        return 2
      }

      return 1
    }

    if (membersMatch) {
      if (hasEvents && hasPresentations) {
        return 3
      }

      if (hasEvents || hasPresentations) {
        return 2
      }

      return 1
    }

    if (
      data.nodeBySlugId?.__typename === 'Guild' &&
      data.nodeBySlugId.type === 'GUILD' &&
      data.nodeBySlugId.totalNetworks.edges.length &&
      networkMatch
    ) {
      if (hasEvents && hasPresentations) {
        return 4
      }

      if (hasEvents || hasPresentations) {
        return 3
      }

      return 2
    }

    return -1
  }, [data, homeMatch, networkMatch, eventsMatch, presentationsMatch, membersMatch])

  const navLabel = useMemo(() => {
    if (homeMatch) return t`Guild`
    if (networkMatch) return t`Network`
    if (eventsMatch) return t`Events`
    if (presentationsMatch) return t`Presentations`
    if (membersMatch) return t`Members`
    return t`Guild`
  }, [homeMatch, networkMatch, eventsMatch, presentationsMatch, membersMatch, t])

  if (data.nodeBySlugId?.__typename !== 'Guild') {
    return null
  }

  return (
    <YStack gap='$4' $gtXs={{ gap: '$8' }}>
      <GuildCard
        guild={data.nodeBySlugId}
        limitDescriptionLines={8}
        showSettingsButton={!!data.nodeBySlugId.myMembership}
        showMoreDialog
      />

      {!!data.nodeBySlugId.donationCurrency &&
        !!data.nodeBySlugId.minimumDonationAmount && (
          <Button
            errorFlash={false}
            // secondary
            onPress={() => {
              const slugId =
                data.nodeBySlugId?.__typename === 'Guild' &&
                data.nodeBySlugId?.slugId

              if (slugId) {
                navigate(`/${slugId}/donate`, {
                  state: { backgroundLocation: location },
                })
              }
            }}
            paddingHorizontal='$5'
            paddingVertical='$2'
            justifyContent='flex-start'
            height={36}
          >
            <XStack alignItems='center' gap='$3'>
              <EmojiIcon name='sparkling-heart' size='$5' />

              <Button.Text size='$5' fontWeight='$6'>
                <Trans>Donate</Trans>
              </Button.Text>
            </XStack>
          </Button>
        )}

      <MobileNavDropdown
        label={navLabel}
        openLabel={t`Navigation`}
        icon={
          <IconWrapper color='$color'>
            <NavigationIcon />
          </IconWrapper>
        }
        $gtXs={{ display: 'none' }}
      >
        <GuildNavigation data={data} activeIndex={selectedIndex} />
      </MobileNavDropdown>

      <GuildNavigation
        data={data}
        activeIndex={selectedIndex}
        display='none'
        $gtXs={{ display: 'flex' }}
      />
    </YStack>
  )
}

const GuildNavigatorSidebar = ({
  data,
  children,
}: {
  data: GuildNavigatorQuery$data
  children: React.ReactNode
}) => {
  if (data.nodeBySlugId?.__typename !== 'Guild') {
    return null
  }

  return (
    <Screen>
      <YStack gap='$8' flex={1} $gtXs={{ display: 'none' }}>
        <GuildNavigatorCardSection data={data} />

        {children}
      </YStack>

      <Grid display='none' $gtXs={{ display: 'flex' }} gap='$8'>
        <Grid.Column flex={5} $gtSm={{ flex: 4 }} $gtMd={{ flex: 3 }}>
          <YStack gap='$4' style={{ position: 'sticky', top: 84 }}>
            <GuildNavigatorCardSection data={data} />
          </YStack>
        </Grid.Column>

        <Grid.Column flex={5} $gtSm={{ flex: 8 }} $gtMd={{ flex: 9 }}>
          {children}
        </Grid.Column>
      </Grid>
    </Screen>
  )
}

const GuildNavigation = Group.styleable<{
  data: GuildNavigatorQuery$data
}>(({ data, ...props }, ref) => {
  const params = useParams()

  if (data.nodeBySlugId?.__typename !== 'Guild') {
    return null
  }

  let currentIndex = 0

  return (
    <Group
      {...props}
      ref={ref}
      gap='$2'
      fullWidthHighlight
      highlightedElementProps={{ borderRadius: '$6' }}
    >
      <Group.Item index={currentIndex++}>
        <Link
          href={`/${params.actorId}`}
          hoverStyle={{ textDecorationLine: 'none' }}
          paddingHorizontal='$5'
          paddingVertical='$2'
        >
          <XStack alignItems='center' gap='$3'>
            <Icon name='home' color='$color' />

            <Text color='$color' fontWeight='$5'>
              <Trans>Guild</Trans>
            </Text>
          </XStack>
        </Link>
      </Group.Item>

      {data.nodeBySlugId.type === 'NETWORK' &&
        !!data.nodeBySlugId.totalGuilds.edges.length && (
          <Group.Item index={currentIndex++}>
            <Link
              href={`/${params.actorId}/network`}
              hoverStyle={{ textDecorationLine: 'none' }}
              paddingHorizontal='$5'
              paddingVertical='$2'
            >
              <XStack alignItems='center' gap='$3'>
                <Icon name='organization' color='$color' />

                <Text color='$color' fontWeight='$5'>
                  Network
                </Text>
              </XStack>
            </Link>
          </Group.Item>
        )}

      {!!data.nodeBySlugId.totalEvents.edges.length && (
        <Group.Item index={currentIndex++}>
          <Link
            href={`/${params.actorId}/events`}
            hoverStyle={{ textDecorationLine: 'none' }}
            paddingHorizontal='$5'
            paddingVertical='$2'
          >
            <XStack alignItems='center' gap='$3'>
              <Icon name='calendar' color='$color' />

              <Text color='$color' fontWeight='$5'>
                <Trans>Events</Trans>
              </Text>
            </XStack>
          </Link>
        </Group.Item>
      )}

      {!!data.nodeBySlugId.totalPresentations.edges.length && (
        <Group.Item index={currentIndex++}>
          <Link
            href={`/${params.actorId}/presentations`}
            hoverStyle={{ textDecorationLine: 'none' }}
            paddingHorizontal='$5'
            paddingVertical='$2'
          >
            <XStack alignItems='center' gap='$3'>
              <Icon name='projection-screen' color='$color' />

              <Text color='$color' fontWeight='$5'>
                <Trans>Presentations</Trans>
              </Text>
            </XStack>
          </Link>
        </Group.Item>
      )}

      <Group.Item index={currentIndex++}>
        <Link
          href={`/${params.actorId}/members`}
          hoverStyle={{ textDecorationLine: 'none' }}
          paddingHorizontal='$5'
          paddingVertical='$2'
        >
          <XStack alignItems='center' gap='$3'>
            <Icon name='people-community' color='$color' />

            <Text color='$color' fontWeight='$5'>
              <Trans>Members</Trans>
            </Text>
          </XStack>
        </Link>
      </Group.Item>

      {data.nodeBySlugId.type === 'GUILD' &&
      data.nodeBySlugId.totalNetworks.edges.length ? (
        <Group.Item index={currentIndex++}>
          <Link
            href={`/${params.actorId}/network`}
            hoverStyle={{ textDecorationLine: 'none' }}
            paddingHorizontal='$5'
            paddingVertical='$2'
          >
            <XStack alignItems='center' gap='$3'>
              <Icon name='organization' color='$color' />

              <Text color='$color' fontWeight='$5'>
                <Trans>Network</Trans>
              </Text>
            </XStack>
          </Link>
        </Group.Item>
      ) : null}
    </Group>
  )
})

export default GuildNavigator
